import type { ApolloCache } from "@apollo/client";

import {
  type GetIssuesWithActionsQuery,
  gql,
} from "@medbillai/generated-graphql-types";

import { getYourCasesQuery } from "../sections/queries";

export const checkLinksQuery = gql(/* GraphQL */ `
  query CheckInsuranceLinks {
    me {
      id
      flexpaLinks {
        id
        active
      }
      webAgentLinks {
        __typename
        id
        active
      }
    }
  }
`);

export const getWelcomePromptsQuery = gql(/* GraphQL */ `
  query GetWelcomePrompts($benefitsSummaryCaseType: String!) {
    getWelcomePrompts {
      type
      completed
    }
    mostRecentBenefitsSummaryCase: me {
      issues(
        filter: {
          caseType: { eq: $benefitsSummaryCaseType }
          events: {
            eventType: {
              eq: BENEFITS_SUMMARY_GENERATED
            }
          }
        }
        order: { openedAt: { dir: DESC } }
        first: 1
      ) {
        id
      }
    }
  }
`);

export const getPersonalizedPromptsQuery = gql(/* GraphQL */ `
  query GetPersonalizedPrompts {
    getPersonalizedPrompts {
      prompts {
        id
        text
        response
        caseType
        icon
      }
    }
  }
`);

export const getIssuesWithActionsQuery = gql(/* GraphQL */ `
  query GetIssuesWithActions {
    me {
      id
      issues(filter: { hasActions: { eq: true }, closedAt: { exists: false } }) {
        id
        title
        openedAt
        caseType
        actions {
          id
          eventType
          actionRequired
        }
      }
    }
  }
`);

export const getMostRecentClosedIssueWithoutFeedbackQuery = gql(/* GraphQL */ `
  query MostRecentIssueWithClosedEventWithoutFeedback($userId: ID!) {
    issue__ClosedEvent(
      filter: { 
        feedback: { exists: false },
        issue: { owner: { id: { eq: $userId } }, closedAt: { exists: true } }
      }
      order: { sentAt: { dir: DESC } }
      first: 1
    ) {
      id
      sentAt
      issue {
        id
        title
        openedAt
        caseType
        dollarAmountSaved
        totalBillDollarAmount
      }
    }
  }
`);

export const showWelcomeScreenQuery = gql(/* GraphQL */ `
  query ShowWelcomeScreen {
    showWelcomeScreen
  }
`);

// The legacy home (GetIssuesWithActions) and the redesigned home (GetYourCases)
// are separate query documents, and only one of them is ever fetched — so the
// resolved action has to be stripped from whichever document is in cache.
type ActionListQueryData = {
  me?: {
    issues: Array<{
      id: string;
      actions?: Array<{ id: string; eventType?: string }> | null;
    }>;
  } | null;
};

export const removeActionFromCache = (
  cache: ApolloCache<unknown>,
  actionId: string,
) => {
  for (const query of [getYourCasesQuery, getIssuesWithActionsQuery]) {
    try {
      cache.updateQuery<ActionListQueryData>({ query }, prev => {
        const hasAction = prev?.me?.issues.some(issue =>
          issue.actions?.some(action => action.id === actionId),
        );
        if (!prev?.me || !hasAction) {
          // Returning null skips the write — the query isn't cached or the
          // action isn't in it.
          return null;
        }
        return {
          ...prev,
          me: {
            ...prev.me,
            issues: prev.me.issues.map(issue => ({
              ...issue,
              actions: (issue.actions ?? []).filter(
                action => action.id !== actionId,
              ),
            })),
          },
        };
      });
    } catch {
      // No-op if the query is not in cache
    }
  }

  pruneActionlessIssuesFromCache(cache);
};

// Owner reply clears staff CHAT actionRequired server-side, but Issue.actions
// still includes unseen staff CHAT (`not exists .seenAt`). Strip every CHAT
// action from home caches so "New message" / "Respond to your case" disappear
// immediately — matching the post-reply server state once seenAt is stamped.
export const clearStaffChatActionsFromHomeCache = (
  cache: ApolloCache<unknown>,
  issueId: string,
) => {
  cache.modify({
    id: cache.identify({ __typename: "issue__Issue_Type", id: issueId }),
    fields: {
      actions(existing: readonly { __ref?: string }[] = [], { readField }) {
        return existing.filter(
          action => readField("eventType", action) !== "CHAT",
        );
      },
    },
  });

  for (const query of [getYourCasesQuery, getIssuesWithActionsQuery]) {
    try {
      cache.updateQuery<ActionListQueryData>({ query }, prev => {
        const issue = prev?.me?.issues.find(item => item.id === issueId);
        const hasChat = issue?.actions?.some(
          action => action.eventType === "CHAT",
        );
        if (!prev?.me || !hasChat) {
          return null;
        }
        return {
          ...prev,
          me: {
            ...prev.me,
            issues: prev.me.issues.map(item =>
              item.id === issueId
                ? {
                    ...item,
                    actions: (item.actions ?? []).filter(
                      action => action.eventType !== "CHAT",
                    ),
                  }
                : item,
            ),
          },
        };
      });
    } catch {
      // No-op if the query is not in cache
    }
  }

  pruneActionlessIssuesFromCache(cache);
};

// The root list is server-filtered on `hasActions`, and Apollo can't re-evaluate
// that after a normalized update — so an issue whose last action just went away
// lingers here, rendering as an actionless row that still counts toward the
// legacy home's casesShownCount/actionableCasesCount.
export const pruneActionlessIssuesFromCache = (cache: ApolloCache<unknown>) => {
  try {
    cache.updateQuery<GetIssuesWithActionsQuery>(
      { query: getIssuesWithActionsQuery },
      prev => {
        if (!prev?.me?.issues) {
          return prev;
        }
        return {
          ...prev,
          me: {
            ...prev.me,
            issues: prev.me.issues.filter(
              issue => (issue.actions?.length ?? 0) > 0,
            ),
          },
        };
      },
    );
  } catch {
    // No-op if the query is not in cache
  }
};
